'use client';

import type { GeneralLayout } from '@eliadmin/core/src/api/general/types/general-layout-types';
import type { TopbarNotificationConfig } from '@eliadmin/core/src/components/header/topbar-notification-types';
import { Container } from '@eliadmin/core/src/components/ui/container';
import { HtmlRenderer } from '@eliadmin/core/src/components/ui/html-renderer';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';
import { TOPBAR_NOTIFICATION_CONFIG } from '@eliadmin/core/src/constants/topbar-notification-config';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Cross2Icon } from '@radix-ui/react-icons';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';

export type TopbarNotificationProps = {
  generalLayout: GeneralLayout['data'];
};

type DismissedPayload = { content: string; dismissedAt: number };

type MarqueeStyle = CSSProperties & { '--ea-topbar-distance'?: string };

// Modra pilot based on core 3.0.121: keep the notification contract, animate only CSS transforms.
function getMarqueeStyle(distance: number, duration: number, enabled: boolean): MarqueeStyle {
  const canAnimate = enabled && Number.isFinite(distance) && distance > 0 &&
    Number.isFinite(duration) && duration > 0;

  return {
    '--ea-topbar-distance': canAnimate ? `${-distance}px` : undefined,
    animationName: canAnimate ? 'ea-modra-topbar-marquee' : 'none',
    animationDuration: canAnimate ? `${duration}s` : undefined,
    animationTimingFunction: 'linear',
    animationIterationCount: 'infinite',
    transform: 'translate3d(0, 0, 0)',
    willChange: canAnimate ? 'transform' : undefined,
  };
}

export const TopbarNotification = ({ generalLayout }: TopbarNotificationProps) => {
  const config = TOPBAR_NOTIFICATION_CONFIG as TopbarNotificationConfig;
  const singleSetRef = useRef<HTMLDivElement | null>(null);
  const [trackWidth, setTrackWidth] = useState(0);
  const [isReducedMotion, setIsReducedMotion] = useState(false);

  const layoutContent = generalLayout.header?.topbar_notification ?? '';
  const messages = useMemo(
    () => (config.messages?.length ? [...config.messages] : layoutContent ? [layoutContent] : []),
    [config.messages, layoutContent],
  );
  const contentKey = messages.join('\u001f');
  const [isVisible, setIsVisible] = useState(
    () => !config.dismissible && messages.some(message => message.length > 0),
  );

  const storageKey = config.storageKey ?? 'ea-topbar-notification-dismissed';
  const dismissDurationMs = config.dismissDurationMs ?? 2 * 24 * 60 * 60 * 1000;
  const respectReducedMotion = config.respectReducedMotion ?? true;

  useEffect(() => {
    if (!contentKey) {
      setIsVisible(false);
      return;
    }

    if (!config.dismissible) {
      setIsVisible(true);
      return;
    }

    try {
      const raw = window.localStorage.getItem(storageKey);

      if (!raw) {
        setIsVisible(true);
        return;
      }

      const parsed = JSON.parse(raw) as DismissedPayload;
      const now = Date.now();

      setIsVisible(parsed.content !== contentKey || now - parsed.dismissedAt > dismissDurationMs);
    } catch {
      setIsVisible(true);
    }
  }, [config.dismissible, contentKey, dismissDurationMs, storageKey]);

  useEffect(() => {
    if (!respectReducedMotion) return;

    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updateReducedMotion = () => setIsReducedMotion(media.matches);
    updateReducedMotion();
    media.addEventListener('change', updateReducedMotion);

    return () => media.removeEventListener('change', updateReducedMotion);
  }, [respectReducedMotion]);

  const measureContentTrack = useCallback(() => {
    if (config.variant !== 'content-marquee' || !singleSetRef.current) return;
    setTrackWidth(singleSetRef.current.scrollWidth);
  }, [config.variant]);

  useEffect(() => {
    if (config.variant !== 'content-marquee' || !isVisible) return;
    const content = singleSetRef.current;
    if (!content) return;

    measureContentTrack();
    const observer = typeof ResizeObserver === 'undefined'
      ? undefined
      : new ResizeObserver(measureContentTrack);
    observer?.observe(content);
    window.addEventListener('resize', measureContentTrack);
    document.fonts?.addEventListener('loadingdone', measureContentTrack);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measureContentTrack);
      document.fonts?.removeEventListener('loadingdone', measureContentTrack);
    };
  }, [config.variant, contentKey, isVisible, measureContentTrack]);

  const canAnimate = isVisible && !!contentKey && !(respectReducedMotion && isReducedMotion);

  const handleClose = () => {
    if (config.dismissible && contentKey) {
      try {
        window.localStorage.setItem(
          storageKey,
          JSON.stringify({
            content: contentKey,
            dismissedAt: Date.now(),
          } satisfies DismissedPayload),
        );
      } catch {
        // Ignore unavailable storage.
      }
    }

    setIsVisible(false);
  };

  const alignmentClass =
    config.messagePosition === 'left'
      ? 'justify-start'
      : config.messagePosition === 'right'
        ? 'justify-end'
        : 'justify-center';

  const renderMessage = (message: string) =>
    config.renderer === 'raw' ? (
      <HtmlRendererRaw
        as="span"
        html={message}
        className={cn('ea-header__topbar-notification__content', config.className?.content)}
      />
    ) : (
      <HtmlRenderer
        html={message}
        className={cn(
          'ea-header__topbar-notification__content font-medium',
          config.className?.content,
        )}
      />
    );

  const renderContentMessageSet = (ariaHidden?: boolean) => (
    <div
      ref={ariaHidden ? undefined : singleSetRef}
      className={cn('flex', config.className?.messageSet)}
      aria-hidden={ariaHidden}
    >
      {messages.map((message, index) => (
        <div
          key={`${message}-${index}`}
          className={cn(
            'flex items-center whitespace-nowrap text-sm text-white sm:text-base',
            'ea-header__topbar-notification__item',
            config.className?.item,
          )}
        >
          {renderMessage(message)}
          {config.separator}
        </div>
      ))}
    </div>
  );

  const renderCloseButton = () =>
    config.dismissible ? (
      <button
        type="button"
        onClick={handleClose}
        aria-label="Close topbar notification"
        className={cn(
          'inline-flex items-center justify-center text-xs text-inherit',
          'hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          config.className?.closeButton,
        )}
      >
        <Cross2Icon className="h-5 w-5" aria-hidden="true" />
      </button>
    ) : null;

  if (!contentKey || !isVisible) return null;

  if (config.variant === 'content-marquee') {
    return (
      <div className={cn(config.className?.root, 'ea-header__topbar-notification')}>
        <div
          className={cn('flex', config.className?.track)}
          style={getMarqueeStyle(trackWidth, config.marqueeDuration ?? 8, canAnimate)}
          data-ea-topbar-reduced-motion={respectReducedMotion ? 'respect' : undefined}
        >
          {renderContentMessageSet()}
          {renderContentMessageSet(true)}
        </div>
      </div>
    );
  }

  if (config.variant === 'viewport-marquee') {
    const repeatCount = config.viewportRepeatCount ?? 3;
    const itemWidth = config.viewportItemWidth ?? 1280;

    return (
      <div className={cn(config.className?.root, 'ea-header__topbar-notification')}>
        <div
          className={cn(
            'flex h-[44px] w-full items-center justify-between gap-3 px-1 sm:px-2',
            config.className?.container,
          )}
        >
          <div className="relative flex flex-1 items-center overflow-hidden">
            <div
              style={getMarqueeStyle(itemWidth, itemWidth / (config.marqueeSpeed ?? 90), canAnimate)}
              data-ea-topbar-reduced-motion={respectReducedMotion ? 'respect' : undefined}
              className={cn(
                'inline-flex w-max items-center whitespace-nowrap will-change-transform',
                'ea-header__topbar-notification__track',
                config.className?.track,
              )}
            >
              {Array.from({ length: repeatCount }, (_, index) => (
                <div
                  key={index}
                  aria-hidden={index > 0 || undefined}
                  className={cn(
                    'flex items-center gap-1 sm:gap-2',
                    'ea-header__topbar-notification__item',
                    config.className?.item,
                  )}
                  style={{ flex: `0 0 ${itemWidth}px` }}
                >
                  {config.icon}
                  {messages.map((message, messageIndex) => (
                    <Fragment key={`${message}-${messageIndex}`}>{renderMessage(message)}</Fragment>
                  ))}
                </div>
              ))}
            </div>
          </div>
          {renderCloseButton()}
        </div>
      </div>
    );
  }

  return (
    <div className={cn(config.className?.root, 'ea-header__topbar-notification')}>
      <Container
        className={cn(
          'flex items-center justify-between gap-3 px-1 text-sm sm:px-2 lg:h-[44px]',
          'ea-header__topbar-notification__container',
          config.className?.container,
        )}
      >
        <div
          className={cn(
            'flex flex-1 items-center gap-1 sm:gap-2',
            alignmentClass,
            config.className?.item,
          )}
        >
          {config.icon}
          {messages.map((message, index) => (
            <Fragment key={`${message}-${index}`}>{renderMessage(message)}</Fragment>
          ))}
        </div>
        {renderCloseButton()}
      </Container>
    </div>
  );
};
